import { useCallback, useEffect, useState } from "react";
import { Loader2, Activity } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { api } from "../../lib/api";

const Kpi = ({ label, visits, uniques, testid }) => {
  const { t } = useLang();
  return (
    <div className="panel kpi" data-testid={testid}>
      <span>{label}</span>
      <b>{visits}</b><small>{t.traffic.visits} · {uniques} {t.traffic.uniques}</small>
    </div>
  );
};

const DailyChart = ({ daily }) => {
  const { t, lang } = useLang();
  const max = Math.max(1, ...daily.map((d) => d.visits));
  return (
    <div className="traffic-chart" role="img" aria-label={t.traffic.daily} data-testid="traffic-daily-chart">
      {daily.map((d) => {
        const label = new Date(`${d.day}T00:00:00Z`).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "2-digit", month: "short" });
        return (
          <div className="traffic-bar-col" key={d.day} title={`${label} — ${d.visits} ${t.traffic.visits}, ${d.uniques} ${t.traffic.uniques}`}>
            <div className="traffic-bar" style={{ height: `${(d.visits / max) * 100}%` }}>
              <i style={{ height: d.visits ? `${(d.uniques / d.visits) * 100}%` : 0 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TopList = ({ title, rows, map, testid }) => {
  const { t } = useLang();
  const total = rows.reduce((a, r) => a + r.count, 0) || 1;
  return (
    <div className="panel pro-panel" data-testid={testid}>
      <p className="console-title" style={{ display: "block", marginBottom: 12 }}>{title}</p>
      {rows.length === 0 ? <p className="empty">{t.traffic.none}</p> : (
        <ul className="top-list">
          {rows.map((r) => (
            <li key={r.key}>
              <span className="top-key">{map ? map[r.key] || r.key : r.key}</span>
              <span className="top-bar"><i style={{ width: `${(r.count / total) * 100}%` }} /></span>
              <b>{r.count}</b>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const Traffic = () => {
  const { t } = useLang();
  const x = t.traffic;
  const [data, setData] = useState(null);
  const load = useCallback(async () => setData((await api.get("/admin/analytics")).data), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (!data) return <Loader2 className="spin" aria-label={t.common.loading} />;
  return (
    <div data-testid="admin-traffic-panel">
      <div className="sub-head" style={{ marginBottom: 16 }}>
        <h3 className="sub-title">{x.title}</h3>
        <span className="live-tag" data-testid="traffic-live"><span className="live-dot" />{data.live} {x.live}</span>
      </div>
      <div className="kpis" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
        <Kpi label={x.today} {...data.today} testid="traffic-kpi-today" />
        <Kpi label={x.d7} {...data.d7} testid="traffic-kpi-7d" />
        <Kpi label={x.d30} {...data.d30} testid="traffic-kpi-30d" />
      </div>
      <div className="panel pro-panel" style={{ marginBottom: 18 }}>
        <div className="sub-head"><p className="console-title">{x.daily}</p>
          <span className="legend" style={{ margin: 0 }}><span><i style={{ background: "var(--amber)" }} />{x.visits}</span><span><i style={{ background: "var(--cyan)" }} />{x.uniques}</span></span>
        </div>
        <DailyChart daily={data.daily} />
      </div>
      <div className="traffic-grid">
        <TopList title={x.topPages} rows={data.top_pages} testid="traffic-top-pages" />
        <TopList title={x.topReferrers} rows={data.top_referrers} testid="traffic-top-referrers" />
        <TopList title={x.devices} rows={data.devices} map={x.deviceNames} testid="traffic-devices" />
        <TopList title={x.languages} rows={data.languages} testid="traffic-languages" />
        {data.countries.length > 0 && <TopList title={x.countries} rows={data.countries} testid="traffic-countries" />}
      </div>
      <p className="info-note"><Activity aria-hidden="true" /><span>{x.privacy}</span></p>
    </div>
  );
};
